// Launches the portal's existing wizards (documents, service record, petition hub, hearing outcome, Menzies pack…) from a step on the map.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GenerateLegalDocDialog } from "@/pages/collections/GenerateLegalDocWizard";
import { ServicePackDialog } from "@/pages/collections/ServicePackDialog";
import ServiceRecordDialog from "@/pages/legal/ServiceRecordDialog";
import ReviewOutcomeDialog from "@/pages/legal/ReviewOutcomeDialog";
import PetitionHubDialog from "@/pages/legal/petition/PetitionHubDialog";
import HearingOutcomeDialog from "@/pages/legal/petition/HearingOutcomeDialog";
import MenziesPackDialog from "@/pages/legal/petition/MenziesPackDialog";
import CourtCostsDialog from "@/pages/legal/petition/CourtCostsDialog";
import AddLegalEventDialog from "@/pages/legal/AddLegalEventDialog";
import GateChecklistDialog from "@/pages/legal/GateChecklistDialog";
import WitnessStatementDialog from "@/pages/legal/witness/WitnessStatementDialog";
import { STAGE_FOR_STEP } from "@/pages/legal/witness/witnessShared";
import { getCaseGates, getLegalDiarySettings, listLegalEventTypes } from "@/lib/api";
import { WIZARDS, today } from "./mapShared";

export function MapWizardHost({ wizard, caseRow, onClose, onChanged }) {
  const w = WIZARDS[wizard.key];
  const kind = w?.kind;
  const [methods, setMethods] = useState(null);
  const [diary, setDiary] = useState(null);
  const [gate, setGate] = useState(null);
  const [reopen, setReopen] = useState(0);
  useEffect(() => {
    const fail = (e) => { toast.error(e?.response?.data?.detail || e.message); onClose(); };
    if (kind === "service_record") getCaseGates(caseRow.case_id).then((g) => setMethods(g.service_methods || [])).catch(fail);
    if (kind === "legal_event") Promise.all([listLegalEventTypes(), getLegalDiarySettings().catch(() => null)]).then(([t, s]) => setDiary({ eventTypes: t.types || [], diarySettings: s })).catch(fail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);
  if (!w) return null;
  const done = () => { onClose(); onChanged?.(); };
  const blocked = (g, ev) => setGate(typeof g === "string" ? g : g?.gate || ev?.gate);
  if (gate) {
    return <GateChecklistDialog caseRow={caseRow} gate={gate} onClose={() => { setGate(null); onClose(); }} proceedLabel="Continue" onPassed={() => { setGate(null); setReopen((n) => n + 1); }} />;
  }
  switch (kind) {
    case "doc":
      return <GenerateLegalDocDialog key={reopen} kind={w.doc} caseRow={caseRow} onClose={done} />;
    case "service_pack":
      return <ServicePackDialog caseRow={caseRow} kind="sd2" debtorName={wizard.party?.name || ""} serviceAddress={wizard.party?.address || ""} onClose={done} />;
    case "service_record":
      if (!methods) return null;
      return <ServiceRecordDialog key={reopen} caseRow={caseRow} methods={methods} document={wizard.step?.step_id === "bk_petition" ? "petition" : "sd2"} onClose={onClose} onSaved={done} onGateBlocked={blocked} />;
    case "review_outcome":
      return <ReviewOutcomeDialog caseRow={caseRow} onClose={onClose} onDone={done} />;
    case "petition_hub":
      return <PetitionHubDialog caseRow={caseRow} onClose={done} onRouteChanged={() => onChanged?.()} />;
    case "hearing_outcome":
      return <HearingOutcomeDialog key={reopen} caseRow={caseRow} onClose={onClose} onSaved={done} onGate={blocked} />;
    case "menzies_pack":
      return <MenziesPackDialog key={reopen} caseRow={caseRow} onClose={done} onDone={done} onGate={blocked} />;
    case "court_costs":
      return <CourtCostsDialog caseRow={caseRow} onClose={done} onChanged={() => onChanged?.()} />;
    case "witness_statement":
      return <WitnessStatementDialog caseRow={caseRow} presetStage={STAGE_FOR_STEP[wizard.step?.step_id]} onClose={onClose} onDone={() => onChanged?.()} />;
    case "legal_event":
      if (!diary) return null;
      return (
        <AddLegalEventDialog open onClose={onClose} eventTypes={diary.eventTypes} defaultDate={today()} diarySettings={diary.diarySettings} caseCourts={caseRow.issuing_courts || null}
          fixedCase={{ case_id: caseRow.case_id, customer_name: caseRow.customer_name, loan_application_id: caseRow.loan_application_id }} onCreated={done} />
      );
    default:
      return null;
  }
}
